import Quiz from '@api/Quiz'
import PageLayout from '@components/page/PageLayout'
import { Button, Checkbox, Flex, Table, Text, TextInput } from '@mantine/core'
import { useEffect, useState } from 'react'
import InputPersonType from '@components/inputs/quiz/InputPersonType'
import TableDeleteButton from '@components/button/TableDeleteButton'
import {
    IRequestDeleteQuiz,
    IResponseGetAllQuizs,
    IResponseGetAllQuizsRowData,
} from '@apiinterface/IQuiz'
import TableTemplate from '@components/table/TableTemplate'
import { ITHeadMap, ITable } from '@interfaces/ITable'
import { modals } from '@mantine/modals'
import { QueryObserverResult } from 'react-query'
import TablePagination from '@components/table/TablePagination'
import InputDate from '@components/inputs/InputDate'
import dayjs from '@config/dayjs'
import {
    IconCheck,
    IconExclamationCircle,
    IconTrash,
} from '@tabler/icons-react'
import api from '@config/api'
import { notifications } from '@mantine/notifications'

const AllData = () => {
    const [selection, setSelection] = useState<number[]>([])
    const [limit, setlimit] = useState(10)
    const [orderby, setorderby] =
        useState<keyof IResponseGetAllQuizsRowData>('quiz_id')
    const [order, setorder] = useState<string>('desc')
    const [page, setpage] = useState(1)
    const [search, setsearch] = useState('')
    const [created_at, setcreated_at] = useState<string>('')
    const [person_type, setperson_type] = useState('')

    const lists = Quiz.useGetAllQuizs({
        limit,
        orderby,
        order,
        page,
        search,
        created_at,
        person_type,
    })

    useEffect(() => {
        lists.refetch()
        // eslint-disable-next-line
    }, [page, limit, order, orderby, person_type, created_at])

    const openBulkDeleteConfirmModal = () => {
        const data: IRequestDeleteQuiz = {
            quiz_ids: selection,
        }
        const request = async () => {
            try {
                const res = await api.delete('/quizs', { data })
                notifications.show({
                    title: res.data.message,
                    message: '',
                    color: 'teal',
                    icon: <IconCheck size='1rem' />,
                })
                lists.refetch()
                setSelection([])
            } catch (error: any) {
                notifications.show({
                    title: 'เกิดข้อผิดพลาด',
                    message: error.response?.data.message,
                    color: 'red',
                    icon: <IconExclamationCircle size='1rem' />,
                })
                setSelection([])
            }
        }
        modals.openConfirmModal({
            title: 'กรุณายืนยันการลบ',
            labels: { confirm: 'ยืนยัน', cancel: 'ยกเลิก' },
            children: <Text>ลบรายการ {selection.length} รายการ</Text>,
            onConfirm: () => {
                request()
            },
            modalId: 'deleteBulk',
        })
    }

    const thead: ITHeadMap<IResponseGetAllQuizsRowData>[] = [
        {
            sortkey: 'quiz_id',
            label: 'ไอดี',
            width: '5%',
        },
        {
            sortkey: 'person_id',
            label: 'รหัสบุคคล',
            width: '5%',
        },
        {
            sortkey: 'person_type',
            label: 'ประเภทบุคคล',
            width: '5%',
        },
        {
            sortkey: 'question_1',
            label: 'Q1 (เป้าหมายชีวิต)',
            width: '17%',
        },
        {
            sortkey: 'question_2',
            label: 'Q2 (เงินออมต่อเดือน)',
            width: '8%',
        },
        {
            sortkey: 'question_3',
            label: 'Q3 (แนวความคิด)',
            width: '22%',
        },
        {
            sortkey: 'question_4',
            label: 'Q4 (เคยคำนวณ)',
            width: '8%',
        },
        {
            sortkey: 'life_insurance_mtl',
            label: 'ประกัน mtl',
            width: '8%',
        },
        {
            sortkey: 'life_insurance_other',
            label: 'ประกันอื่นๆ',
            width: '8%',
        },
        {
            sortkey: 'click_tracking_1',
            label: 'คลิกค้นหา',
            width: '8%',
        },
        {
            sortkey: 'created_at',
            label: 'สร้างเมื่อ',
            width: '10%',
        },
        {
            label: 'Action',
            width: '5%',
        },
    ]

    return (
        <PageLayout pageTitle='ข้อมูลแบบประเมินเงินออมเพื่อรองรับการเกษียณอายุ'>
            <Flex gap='md' direction={{ xs: 'column', lg: 'row' }}>
                <TextInput
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            console.log('Enter')
                            lists.refetch()
                        }
                    }}
                    w='100%'
                    value={search}
                    onChange={(event) => setsearch(event.target.value)}
                    placeholder='ค้นหา'
                    label='ค้นหารายการ'
                />
                <InputPersonType
                    label='ประเภทบุคคล'
                    withAll
                    miw='180px'
                    w='100%'
                    value={String(person_type)}
                    onChange={(value) => {
                        if (value !== String(person_type)) {
                            setperson_type(String(value))
                            setpage(1)
                        }
                    }}
                />
                <InputDate
                    clearable
                    miw='180px'
                    label='สร้างเมื่อ'
                    placeholder='วันที่'
                    value={created_at! as any}
                    onChange={(value) => {
                        if (value !== (created_at as any)) {
                            setcreated_at(value as any)
                            setpage(1)
                        }
                    }}
                />
            </Flex>
            {selection.length ? (
                <Flex
                    mt='lg'
                    py='xs'
                    px='md'
                    align='center'
                    justify='space-between'
                    style={{ borderRadius: '10px' }}
                    bg='blue'
                >
                    <Text c='text.0'>เลือก {selection.length} รายการ</Text>
                    <Button
                        py={0}
                        onClick={openBulkDeleteConfirmModal}
                        leftSection={<IconTrash size={14} />}
                        variant='filled'
                    >
                        ลบ
                    </Button>
                </Flex>
            ) : (
                ''
            )}
            <TableTemplate<IResponseGetAllQuizsRowData>
                thead={thead}
                KeyID={'quiz_id'}
                orderby={orderby}
                order={order}
                setorder={setorder}
                setorderby={setorderby}
                lists={lists?.data?.lists}
                setSelection={setSelection}
                selection={selection}
            >
                {lists?.data?.lists.map((value) => {
                    return (
                        <AllDataTableRow
                            selection={selection}
                            setSelection={setSelection}
                            key={value.quiz_id}
                            data={value}
                            refreshTable={lists.refetch}
                        />
                    )
                })}
            </TableTemplate>
            {lists.data?.lists.length ? (
                <TablePagination
                    limit={lists.data?.lists.length}
                    setlimit={setlimit}
                    setpage={setpage}
                    page={page}
                    totalPages={lists.data.totalPages}
                    totalRows={lists.data.totalRows}
                />
            ) : (
                <></>
            )}
        </PageLayout>
    )
}

const AllDataTableRow = ({
    data,
    refreshTable,
    selection,
    setSelection,
}: {
    data: IResponseGetAllQuizsRowData
    selection: ITable<IResponseGetAllQuizsRowData>['selection']
    setSelection: ITable<IResponseGetAllQuizsRowData>['setSelection']
    refreshTable: () => Promise<
        QueryObserverResult<IResponseGetAllQuizs, unknown>
    >
}) => {
    const reqDeleteProjectVendor = Quiz.useDeleteQuizById({
        quiz_id: data.quiz_id,
        aftersuccess: () => {
            refreshTable()
            setSelection([])
        },
    })
    const toggleRow = (id: number) =>
        setSelection((current) =>
            current.includes(id)
                ? current.filter((item) => item !== id)
                : [...current, id]
        )
    const openDeleteConfirmModal = () =>
        modals.openConfirmModal({
            title: 'กรุณายืนยันการลบ',
            labels: { confirm: 'ยืนยัน', cancel: 'ยกเลิก' },
            children: <Text>ลบรายการข้อมูลไอดี {data.quiz_id}</Text>,
            onConfirm: reqDeleteProjectVendor.onSubmit,
        })

    return (
        <tr key={data.quiz_id}>
            <Table.Td>
                <Checkbox
                    checked={selection.includes(data.quiz_id)}
                    onChange={() => toggleRow(data.quiz_id)}
                />
            </Table.Td>
            <td>{data.quiz_id}</td>
            <td>{data.person_id}</td>
            <td>{data.person_type}</td>
            <td>
                {data.question_1}
                {data.question_1_other != null && ` (${data.question_1_other})`}
            </td>
            <td>{data.question_2}</td>
            <td>{data.question_3}</td>
            <td>
                <Checkbox checked={data.question_4 === 'เคย'} />
            </td>
            <td>
                <Checkbox checked={data.life_insurance_mtl === 'T'} />
            </td>
            <td>
                <Checkbox checked={data.life_insurance_other === 'T'} />
            </td>
            <td>
                <Checkbox checked={data.click_tracking_1 === 'T'} />
            </td>
            <td> {dayjs(data.created_at).fromNow()}</td>
            <td>
                <Flex gap='xs' pos='relative'>
                    <TableDeleteButton onClick={openDeleteConfirmModal} />
                </Flex>
            </td>
        </tr>
    )
}

export default AllData
